import React from 'react';
import { ProgressBar, OverlayTrigger, Tooltip } from 'react-bootstrap';

const ActionAreaTrafficLight = ({ data, tooltipContainerRef }) => {
    const noStatusCount = data?.noStatus || 0;
    const notAchievedCount = data?.notAchieved || 0;
    const partiallyAchievedCount = data?.partiallyAchieved || 0;
    const achievedCount = data?.achieved || 0;

    const totalCount = noStatusCount + notAchievedCount + partiallyAchievedCount + achievedCount;

    if (totalCount === 0) {
        return <div>Keine Einträge</div>;
    }

    const pNoStatus = totalCount > 0 ? (noStatusCount / totalCount) * 100 : 0;
    const pNotAchieved = totalCount > 0 ? (notAchievedCount / totalCount) * 100 : 0;
    const pPartiallyAchieved = totalCount > 0 ? (partiallyAchievedCount / totalCount) * 100 : 0;
    const pAchieved = totalCount > 0 ? (achievedCount / totalCount) * 100 : 0;

    let adjNoStatusPercent = Math.round(pNoStatus);
    let adjNotAchievedPercent = Math.round(pNotAchieved);
    let adjPartiallyAchievedPercent = Math.round(pPartiallyAchieved);
    let adjAchievedPercent = Math.round(pAchieved);

    const sumRounded = adjNoStatusPercent + adjNotAchievedPercent + adjPartiallyAchievedPercent + adjAchievedPercent;

    if (totalCount > 0 && sumRounded !== 100 && sumRounded !== 0) {
        const diff = 100 - sumRounded;
        const max = Math.max(
            adjNoStatusPercent,
            adjNotAchievedPercent,
            adjPartiallyAchievedPercent,
            adjAchievedPercent
        );

        if (adjNoStatusPercent === max) {
            adjNoStatusPercent += diff;
        } else if (adjNotAchievedPercent === max) {
            adjNotAchievedPercent += diff;
        } else if (adjPartiallyAchievedPercent === max) {
            adjPartiallyAchievedPercent += diff;
        } else {
            adjAchievedPercent += diff;
        }
    }

    adjNoStatusPercent = Math.max(0, Math.min(100, adjNoStatusPercent));
    adjNotAchievedPercent = Math.max(0, Math.min(100, adjNotAchievedPercent));
    adjPartiallyAchievedPercent = Math.max(0, Math.min(100, adjPartiallyAchievedPercent));
    adjAchievedPercent = Math.max(0, Math.min(100, adjAchievedPercent));

    const instanceId = data?.id || 'actionarea';
    const tooltipIds = {
        noStatus: `aatl-tooltip-noStatus-${instanceId}`,
        notAchieved: `aatl-tooltip-notAchieved-${instanceId}`,
        partiallyAchieved: `aatl-tooltip-partiallyAchieved-${instanceId}`,
        achieved: `aatl-tooltip-achieved-${instanceId}`,
        noData: `aatl-tooltip-noData-${instanceId}`,
    };

    const segmentStyles = {
        noStatus: { backgroundColor: 'var(--status-no-status)' },
        notAchieved: { backgroundColor: 'var(--status-negative)' },
        partiallyAchieved: { backgroundColor: 'var(--status-positive)' },
        achieved: { backgroundColor: 'var(--status-overachieved)' }
    };

    if (totalCount === 0) {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip id={tooltipIds.noData}>
                        Keine Daten vorhanden
                    </Tooltip>
                }
                container={tooltipContainerRef?.current}
            >
                <ProgressBar style={{ height: '24px' }}>
                    <ProgressBar variant="secondary" now={100} key="empty-bar-segment" />
                </ProgressBar>
            </OverlayTrigger>
        );
    }

    return (
        <div className="progress" style={{ height: '24px' }}>
            {adjNoStatusPercent > 0 && (
                <OverlayTrigger
                    key="noStatus-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.noStatus}>
                            keine Angabe: {adjNoStatusPercent}% ({noStatusCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjNoStatusPercent}%`, ...segmentStyles.noStatus }}
                        aria-valuenow={adjNoStatusPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjNotAchievedPercent > 0 && (
                <OverlayTrigger
                    key="notAchieved-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.notAchieved}>
                            nicht erreicht: {adjNotAchievedPercent}% ({notAchievedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjNotAchievedPercent}%`, ...segmentStyles.notAchieved }}
                        aria-valuenow={adjNotAchievedPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjPartiallyAchievedPercent > 0 && (
                <OverlayTrigger
                    key="partiallyAchieved-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.partiallyAchieved}>
                            erreicht: {adjPartiallyAchievedPercent}% ({partiallyAchievedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjPartiallyAchievedPercent}%`, ...segmentStyles.partiallyAchieved }}
                        aria-valuenow={adjPartiallyAchievedPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
            {adjAchievedPercent > 0 && (
                <OverlayTrigger
                    key="achieved-segment-trigger"
                    placement="top"
                    overlay={
                        <Tooltip id={tooltipIds.achieved}>
                            mehr als erreicht: {adjAchievedPercent}% ({achievedCount})
                        </Tooltip>
                    }
                    container={tooltipContainerRef?.current}
                >
                    <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${adjAchievedPercent}%`, ...segmentStyles.achieved }}
                        aria-valuenow={adjAchievedPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                    />
                </OverlayTrigger>
            )}
        </div>
    );
};

export default ActionAreaTrafficLight;
